/**
 * GET /api/health/brain — client wire contract, PRIORITY 3.
 *
 * `AboutScreen.tsx` fetches this by hand, outside the SDK, and reads a NESTED
 * `version` object. Its own comment records why:
 *
 *     // /api/health/brain returns a NESTED `version` object (not flat) — see
 *     // BrainHealthResponse. Reading it flat produced "[object Object]+undefined".
 *
 * So this shape has ALREADY broken this screen once. Flattening `version`, or
 * moving `version.version` up a level, reproduces that exact string in front of
 * a user.
 *
 * THE UNREACHABLE BRANCH IS PART OF THE CONTRACT
 * The body has the same shape whatever the status (503 when brain is down or
 * unhealthy). When brain is down it returns
 * `status: 'unreachable'` with `health: null` and `version: null` — so a
 * schema that made `version` a required object would fail against a perfectly
 * correct response every time brain restarts. The union below is what keeps
 * this honest: both branches are valid, and the About screen must survive both.
 */
import { z } from 'zod';

/**
 * Brain's own version payload, nested under `version`. Only `version` itself is
 * required — `buildSha` and `openai_model` are read defensively by the screen,
 * and `openai_model` is snake_case on the wire (it comes from brain's own
 * config surface, not from a camelCase DTO). Renaming it to `openaiModel`
 * would silently blank that row.
 */
export const clientWireBrainVersionSchema = z.object({
  version: z.string(),
  buildSha: z.string().optional(),
  openai_model: z.string().optional(),
});

export const clientWireHealthBrainSchema = z.union([
  z.object({
    status: z.literal('reachable'),
    latencyMs: z.number(),
    health: z.record(z.string(), z.unknown()),
    /** NESTED. Flattening this is the regression the screen's comment documents. */
    version: clientWireBrainVersionSchema,
    timestamp: z.string(),
  }),
  z.object({
    status: z.literal('unreachable'),
    latencyMs: z.number(),
    error: z.string(),
    health: z.null(),
    version: z.null(),
    timestamp: z.string(),
  }),
]);

export type ClientWireHealthBrain = z.infer<typeof clientWireHealthBrainSchema>;
